import { defineStore } from 'pinia';
import { getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import { useLogger } from '../composables/useLogger';

type UserRole = 'readonly' | 'developer' | 'deployment' | 'admin';

interface User {
  email: string;
  username: string;
}

interface UserProfile {
  email: string;
  username: string;
  groups: UserRole[];
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    isAdmin: boolean;
  };
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  userGroups: UserRole[];
}

const logger = useLogger('authStore');

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    isAuthenticated: false,
    user: null,
    userGroups: []
  }),
  
  getters: {
    isAdmin: (state): boolean => state.userGroups.includes('admin'),
    isDeployment: (state): boolean => state.userGroups.includes('deployment'),
    isDeveloper: (state): boolean => state.userGroups.includes('developer'),
    isReadonly: (state): boolean => 
      state.userGroups.includes('readonly') && !state.userGroups.includes('developer'),
    canEdit: (state): boolean => 
      state.userGroups.some(g => ['admin', 'deployment', 'developer'].includes(g)),
    canDelete: (state): boolean => 
      state.userGroups.some(g => ['admin', 'deployment', 'developer'].includes(g)),
    displayGroups: (state): UserRole[] => 
      state.userGroups.filter(g => ['readonly', 'developer', 'deployment', 'admin'].includes(g)),
    userProfile(): UserProfile {
      return {
        email: this.user?.email ?? '',
        username: this.user?.username ?? '',
        groups: this.userGroups,
        permissions: {
          canEdit: this.canEdit,
          canDelete: this.canDelete,
          isAdmin: this.isAdmin,
        },
      };
    },
    hasPermission(): (permission: string) => boolean {
      return (permission: string): boolean => {
        switch (permission) {
          case 'edit':
            return this.canEdit;
          case 'delete':
            return this.canDelete;
          case 'admin':
            return this.isAdmin;
          case 'deploy':
          case 'deployment':
            return this.isDeployment;
          case 'develop':
          case 'developer':
            return this.isDeveloper;
          case 'read':
          case 'readonly':
            return this.isAuthenticated;
          default:
            return false;
        }
      };
    }
  },
  
  actions: {
    async checkAuthState() {
      try {
        await getCurrentUser();
        this.isAuthenticated = true;
      } catch {
        this.isAuthenticated = false;
        this.user = null;
        this.userGroups = [];
      }
    },
    
    async loadUserInfo() {
      try {
        // Import the SAML user fix utility
        const { extractSamlUserInfo } = await import('../utils/samlUserFix');
        const userInfo = await extractSamlUserInfo();
        
        this.user = {
          email: userInfo.email,
          username: userInfo.name
        };
        this.userGroups = userInfo.groups as UserRole[];
      } catch (error) {
        logger.error('Failed to load user info', { error: String(error) });
        this.user = null;
        this.userGroups = [];
      }
    },
    
    async signOut() {
      try {
        await amplifySignOut({ global: true });
        this.isAuthenticated = false;
        this.user = null;
        this.userGroups = [];
      } catch (error) {
        logger.error('Failed to sign out', { error: String(error) });
        throw error;
      }
    }
  }
});
